import React from "react";
import { useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import {
  selectUserCartItems,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
  clearCart,
} from "../../features/store/cartSlice";
import { Delete } from "../../assets/DirectTopUpIcon";
import { useAppSelector } from "../../hooks/hooks";
import {
  calculateDGCoinAmount,
  convertCurrencyAmount,
  formatCurrency,
} from "../../utils/utils";

interface CartItemsProps {
  userId: string;
}

const CartItems: React.FC<CartItemsProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const items = useAppSelector((state: RootState) =>
    selectUserCartItems(state, userId),
  );

  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency } = useAppSelector((state) => state.locale);

  const currencies = appData?.currencies || [];

  const dgCoinCurrency =
    currencies.find((currency: any) => currency.code.toLowerCase() === "dgc") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg coin"),
    );

  const dgPointCurrency =
    currencies.find((currency: any) => currency.code.toLowerCase() === "dgp") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg point"),
    );

  const handleRemove = (itemId: string) => {
    dispatch(removeFromCart({ userId, itemId }));
  };

  const handleIncrement = (itemId: string) => {
    dispatch(incrementQuantity({ userId, itemId }));
  };

  const handleDecrement = (itemId: string, currentQuantity: number) => {
    if (currentQuantity <= 1) {
      dispatch(removeFromCart({ userId, itemId }));
    } else {
      dispatch(decrementQuantity({ userId, itemId }));
    }
  };

  const handleClearCart = () => {
    dispatch(clearCart({ userId }));
  };

  const getCurrencyById = (currencyId?: number) =>
    currencies.find((c: any) => c.id === currencyId) ||
    selectedCurrency ||
    currencies[0];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <p className="text-lg font-semibold text-dark dark:text-white">
          Cart Items
        </p>

        {items.length > 0 && (
          <button
            type="button"
            onClick={handleClearCart}
            className="text-xs font-semibold text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors py-1 px-2.5 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20"
          >
            Clear Cart
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">Your cart is empty.</p>
      ) : (
        <div className="flex flex-col gap-4 w-full">
          {items.map((item) => {
            const fromCurrency = getCurrencyById(item.priceCurrencyId);
            const toCurrency = selectedCurrency || fromCurrency;
            const symbol = toCurrency?.symbol || "";

            const convertedUnitPrice = convertCurrencyAmount(
              item.price,
              fromCurrency,
              toCurrency,
            );

            const convertedOriginalPrice =
              item.originalPrice && item.originalPrice > item.price
                ? convertCurrencyAmount(
                    item.originalPrice,
                    fromCurrency,
                    toCurrency,
                  )
                : null;

            const rawCoins = calculateDGCoinAmount(
              item.price,
              item.priceCurrencyId,
              currencies,
              dgCoinCurrency,
            );

            const rawPoints = calculateDGCoinAmount(
              item.price,
              item.priceCurrencyId,
              currencies,
              dgPointCurrency,
            );

            const formattedCoins = formatCurrency(rawCoins * item.quantity);
            const formattedPoints = formatCurrency(rawPoints * item.quantity);

            return (
              <div
                key={item.id}
                className="flex flex-row items-center gap-2 sm:gap-3 w-full"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 bp:w-24 bp:h-24 rounded-md object-cover flex-shrink-0"
                />
                <div className="w-full flex items-center justify-between dark:bg-[#FFFFFF1A] gap-2 border border-border-border1 dark:border-tabs p-2 bp:p-4 rounded-lg">
                  <div className="flex flex-col w-full">
                    <p className="text-xs bp:text-sm font-semibold text-dark dark:text-white">
                      {item.name}
                    </p>
                    <p className="text-[8px] bp:text-xs text-gray-400">
                      Product: {item.product}
                    </p>

                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div className="bg-[#039EF2] text-white px-2 py-0.5 rounded-2xl flex items-center gap-1">
                        <img
                          src="/png/dg.png"
                          alt="Coins"
                          className="w-3.5 h-3.5"
                        />
                        <p className="text-[10px] bp:text-xs font-semibold">
                          {formattedCoins}
                        </p>
                      </div>

                      <div className="bg-[#039EF2] text-white px-2 py-0.5 rounded-2xl flex items-center gap-1">
                        <img
                          src="/images/diamond.png"
                          alt="Points"
                          className="w-3.5 h-3.5"
                        />
                        <p className="text-[10px] bp:text-xs font-semibold">
                          {formattedPoints} pts
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        <span className="text-xs bp:text-sm text-black dark:text-white font-medium">
                          {symbol}{" "}
                          {formatCurrency(convertedUnitPrice * item.quantity)}
                        </span>

                        {convertedOriginalPrice && (
                          <span className="text-[10px] bp:text-xs text-gray-400 line-through">
                            {symbol}{" "}
                            {formatCurrency(
                              convertedOriginalPrice * item.quantity,
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end justify-between gap-4 flex-shrink-0">
                    <button
                      onClick={() => handleRemove(item.id)}
                      className="hover:opacity-75 transition-opacity"
                      aria-label="Remove item"
                    >
                      <Delete />
                    </button>

                    {/* Stepper Controls */}
                    <div className="flex items-center border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => handleDecrement(item.id, item.quantity)}
                        className="px-2 py-0.5 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                        -
                      </button>

                      <span className="px-2 text-xs font-semibold text-black dark:text-white">
                        {item.quantity}
                      </span>

                      <button
                        type="button"
                        onClick={() => handleIncrement(item.id)}
                        className="px-2 py-0.5 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CartItems;
